import React, { useState, useRef, useEffect } from 'react';
import HeroVideoModal from './HeroVideoModal';

/**
 * The prompt field, with its tools, usable OUTSIDE a project.
 *
 * HeroPrompt owns the same composer but is bound to a live project: it
 * calls chatRef.current.sendFromHero(...) and hands ReferenceDesignModal a
 * projectId. On the New Project screen neither exists yet — the project is
 * created by the same click that submits the prompt — so this collects the
 * prompt and its attachments and hands them back, letting the caller decide
 * when to create and when to generate.
 *
 * ReferenceDesignModal is the exception: it POSTs a job against a real
 * projectId and has no prompt-only mode, so 🎨 reports the request
 * upward (onRequestReference) rather than opening the modal here.
 *
 * Controlled: the parent owns `value` so the prompt can be sent with the
 * create request. Everything else (hero video, strict scope, reference) is
 * reported through onChangeOptions.
 */
export default function PromptComposer({
  value,
  onChange,
  onSubmit,
  onOpenImport,
  onChangeOptions,
  onRequestReference,
  placeholder = 'Describe your app…',
  minHeight = 104,
  disabled = false,
  submitLabel = 'Create & Open Workspace',
  showSubmit = true,
}) {
  const textareaRef = useRef(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [heroVideoAttachment, setHeroVideoAttachment] = useState(null);

  // Same persisted flag as HeroPrompt's 🎯 and the in-chat toggle, so the
  // setting follows the user between screens instead of resetting here.
  const [strictScope, setStrictScope] = useState(() => {
    try { return localStorage.getItem('strict_scope') === '1'; } catch { return false; }
  });
  const toggleStrictScope = () => {
    const next = !strictScope;
    setStrictScope(next);
    try { localStorage.setItem('strict_scope', next ? '1' : '0'); } catch { /* blocked storage */ }
  };

  // Report the attachments up so the caller can pass them to generation.
  useEffect(() => {
    onChangeOptions && onChangeOptions({ heroVideoAttachment, strictScope });
  // Intentionally not depending on onChangeOptions: it is a parent
  // callback whose identity changes every render, which would make this
  // fire on every render instead of when an attachment actually changes.
  }, [heroVideoAttachment, strictScope]);

  const autoGrow = (el) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 220) + 'px';
  };
  useEffect(() => { autoGrow(textareaRef.current); }, [value]);

  const handleKeyDown = (e) => {
    // Enter submits, Shift+Enter is a newline — same contract as the hero.
    if (e.key === 'Enter' && !e.shiftKey && onSubmit) {
      e.preventDefault();
      onSubmit();
    }
  };

  return (
    <div className="pc-wrap">
      <style>{`
        .pc-wrap{ position:relative; }
        .pc-shell{
          border:1px solid var(--border); border-radius:14px;
          background:var(--bg-input); padding:12px 14px 10px;
          transition:border-color 0.15s, box-shadow 0.15s;
        }
        .pc-shell:focus-within{
          border-color:rgba(99,102,241,0.55);
          box-shadow:0 0 0 3px rgba(99,102,241,0.09);
        }
        .pc-textarea{
          width:100%; border:none; outline:none; resize:none;
          background:transparent; color:var(--text-primary);
          font-family:inherit; font-size:14px; line-height:1.6;
        }
        .pc-textarea::placeholder{ color:var(--text-muted); }
        .pc-toolbar{
          display:flex; align-items:center; justify-content:space-between;
          gap:10px; margin-top:8px;
        }
        .pc-tools{ display:flex; align-items:center; gap:8px; min-width:0; }
        .pc-tool-btn{
          width:34px; height:34px; border-radius:9px;
          background:var(--bg-primary); border:1px solid var(--border);
          color:var(--text-secondary); font-size:16px; cursor:pointer;
          display:flex; align-items:center; justify-content:center;
          transition:border-color 0.15s, background 0.15s, color 0.15s;
          flex-shrink:0; padding:0; font-family:inherit;
        }
        .pc-tool-btn:hover:not(:disabled){
          border-color:rgba(99,102,241,0.45);
          background:rgba(99,102,241,0.08);
          color:var(--accent-light);
        }
        .pc-tool-btn:disabled{ opacity:0.35; cursor:not-allowed; }
        .pc-tool-btn-active{
          border-color:rgba(99,102,241,0.6) !important;
          background:rgba(99,102,241,0.12) !important;
          color:var(--accent-light) !important;
        }
        .pc-hint{
          font-size:11px; color:var(--text-muted); white-space:nowrap;
          overflow:hidden; text-overflow:ellipsis;
        }
        .pc-send-btn{
          padding:0 18px; height:36px;
          background:linear-gradient(135deg,#6366f1,#8b5cf6);
          color:#fff; border:none; border-radius:10px;
          font-size:13px; font-weight:700; cursor:pointer;
          font-family:inherit; white-space:nowrap; flex-shrink:0;
          box-shadow:0 2px 12px rgba(99,102,241,0.32);
          transition:opacity 0.15s, transform 0.1s;
          display:flex; align-items:center; gap:6px;
        }
        .pc-send-btn:hover:not(:disabled){ opacity:0.88; transform:scale(0.97); }
        .pc-send-btn:disabled{ opacity:0.38; cursor:not-allowed; box-shadow:none; }
        .pc-chip{
          display:flex; align-items:center; justify-content:space-between; gap:8px;
          margin-top:8px; padding:6px 10px; border-radius:8px; font-size:12px;
          background:rgba(99,102,241,0.08); border:1px solid rgba(99,102,241,0.2);
          color:var(--accent-light);
        }
        .pc-chip button{
          background:none; border:none; color:inherit; cursor:pointer;
          font-size:13px; padding:0 2px;
        }
        @media (max-width:640px){ .pc-hint{ display:none; } }
      `}</style>

      <div className="pc-shell">
        <textarea
          ref={textareaRef}
          className="pc-textarea"
          style={{ minHeight }}
          placeholder={placeholder}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
        />

        {heroVideoAttachment && (
          <div className="pc-chip">
            <span>
              🎬 {heroVideoAttachment.heroStyle === 'none' ? 'No hero visual' : 'Hero video'} attached
              {' '}— will be used on generate
            </span>
            <button onClick={() => setHeroVideoAttachment(null)} title="Remove">✕</button>
          </div>
        )}

        <div className="pc-toolbar">
          <div className="pc-tools">
            <button
              type="button"
              className="pc-tool-btn"
              onClick={() => onOpenImport && onOpenImport()}
              disabled={disabled || !onOpenImport}
              title="Import repository"
              aria-label="Import repository"
            >+</button>
            <button
              type="button"
              className="pc-tool-btn"
              onClick={() => onRequestReference && onRequestReference()}
              disabled={disabled || !onRequestReference}
              title="Build from design reference"
              aria-label="Build from design reference"
            >🎨</button>
            <button
              type="button"
              className={`pc-tool-btn${heroVideoAttachment ? ' pc-tool-btn-active' : ''}`}
              onClick={() => setShowVideoModal(true)}
              disabled={disabled}
              title="Hero video / style"
              aria-label="Hero video / style"
            >🎬</button>
            <button
              type="button"
              className={`pc-tool-btn${strictScope ? ' pc-tool-btn-active' : ''}`}
              onClick={toggleStrictScope}
              disabled={disabled}
              aria-pressed={strictScope}
              title={strictScope
                ? 'Match my prompt only: ON — builds only what you ask for (faster, fewer tokens). Click to turn off.'
                : 'Match my prompt only: OFF — builds a full production-ready site with extra pages. Click to build only what you ask for.'}
              aria-label="Match my prompt only"
            >🎯</button>
            <span className="pc-hint">Enter to create · Shift+Enter for new line</span>
          </div>

          {showSubmit && (
            <button
              type="button"
              className="pc-send-btn"
              onClick={onSubmit}
              disabled={disabled}
              title={submitLabel}
            >
              <span style={{ fontSize: 14 }}>⚡</span>{submitLabel}
            </button>
          )}
        </div>
      </div>

      {showVideoModal && (
        <HeroVideoModal
          onClose={() => setShowVideoModal(false)}
          onAttached={(attachment) => { setHeroVideoAttachment(attachment); setShowVideoModal(false); }}
        />
      )}
    </div>
  );
}
